import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Outlet, useNavigate, useParams, useOutletContext } from 'react-router-dom';
import {
  Archive,
  Filter,
  Inbox,
  MessageSquare,
  Plus,
  RefreshCw,
  Search,
  Send,
  UserRound,
  X
} from 'lucide-react';
import {
  createMessageThread,
  getMessageThreads,
  getTeachersAndLearners,
  getUnreadMessageCount,
  searchMessages
} from '../../api/messagingApi';
import './Messages.css';

type Mailbox = 'inbox' | 'sent' | 'archived';
type ReadFilter = 'all' | 'unread' | 'read';

interface Thread {
  threadId: number;
  subject: string;
  status: string;
  isArchived: number;
  lastMessageAt: string;
  createdAt: string;
  teacherName?: string;
  teacherId?: number;
  teacherRole?: string;
  learnerName: string;
  learnerId: number;
  className?: string | null;
  unreadCount: number;
  lastMessage: string;
  lastSenderName?: string;
  lastSenderId?: number;
}

interface Learner {
  learnerId: number;
  learnerName: string;
  classId: number | null;
  className: string | null;
  classTeacherId: number | null;
  classTeacherName: string | null;
  classTeacherRole: string | null;
}

interface Headteacher {
  id: number;
  name: string;
}

interface MessagesOutletContext {
  unreadCount: number;
}

interface WorkspaceOutletContext extends MessagesOutletContext {
  refreshThreads: (options?: { silent?: boolean }) => Promise<void>;
  fetchUnreadCount: () => Promise<void>;
  activeBox: Mailbox;
}

const mailboxConfig: Array<{
  id: Mailbox;
  label: string;
  description: string;
  icon: React.ComponentType<{ size?: number | string; className?: string }>;
}> = [
  { id: 'inbox', label: 'Inbox', description: 'Incoming conversations', icon: Inbox },
  { id: 'sent', label: 'Sent', description: 'Messages you started or replied to', icon: Send },
  { id: 'archived', label: 'Archived', description: 'Stored conversations', icon: Archive }
];

const getRole = () => (localStorage.getItem('role') || '').toLowerCase();
const getDisplayName = () =>
  localStorage.getItem('name') ||
  localStorage.getItem('userName') ||
  localStorage.getItem('fullName') ||
  'Current user';

const formatThreadTimestamp = (value?: string) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const now = new Date();
  const sameDay = date.toDateString() === now.toDateString();
  return sameDay
    ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const buildThreadPreview = (thread: Thread) => {
  if (!thread.lastMessage) {
    return 'Open this conversation to view messages.';
  }
  return thread.lastMessage;
};

const ComposePanel: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSent: () => Promise<void>;
}> = ({ isOpen, onClose, onSent }) => {
  const role = getRole();
  const isStudent = role === 'student';
  const isStaff = role === 'teacher' || role === 'headteacher' || role === 'admin';
  const [learners, setLearners] = useState<Learner[]>([]);
  const [headteacher, setHeadteacher] = useState<Headteacher | null>(null);
  const [selectedLearner, setSelectedLearner] = useState<number | null>(null);
  const [learnerSearch, setLearnerSearch] = useState('');
  const [recipientType, setRecipientType] = useState<'classTeacher' | 'headteacher'>('classTeacher');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen || (!isStudent && !isStaff)) {
      return;
    }

    let active = true;
    const loadRecipients = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await getTeachersAndLearners();
        if (!active) {
          return;
        }

        const nextLearners = data.learners || [];
        setLearners(nextLearners);
        setHeadteacher(data.headteacher || null);

        if (nextLearners.length > 0) {
          setSelectedLearner((current) => current || nextLearners[0].learnerId);
          if (isStudent && !nextLearners[0].classTeacherId && data.headteacher) {
            setRecipientType('headteacher');
          }
        } else {
          setSelectedLearner(null);
        }
      } catch (loadError: any) {
        if (!active) {
          return;
        }
        setError(loadError?.response?.data?.message || 'Failed to load messaging recipients');
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadRecipients();
    return () => {
      active = false;
    };
  }, [isOpen, isStaff, isStudent]);

  useEffect(() => {
    if (!isOpen) {
      setSubject('');
      setBody('');
      setError('');
      setLearnerSearch('');
    }
  }, [isOpen]);

  const filteredLearners = useMemo(() => {
    const query = learnerSearch.trim().toLowerCase();
    if (!query) {
      return learners;
    }

    return learners.filter((learner) => {
      const learnerName = learner.learnerName?.toLowerCase() || '';
      const className = learner.className?.toLowerCase() || '';
      return learnerName.includes(query) || className.includes(query);
    });
  }, [learnerSearch, learners]);

  const selectedLearnerRecord = learners.find((learner) => learner.learnerId === selectedLearner) || null;

  const recipientLabel = useMemo(() => {
    if (!isStudent) {
      return selectedLearnerRecord
        ? `${selectedLearnerRecord.learnerName}${selectedLearnerRecord.className ? ` - ${selectedLearnerRecord.className}` : ''}`
        : 'Select a student';
    }

    if (recipientType === 'headteacher') {
      return headteacher ? headteacher.name : 'Headteacher not available';
    }

    return selectedLearnerRecord?.classTeacherName || 'Class teacher not assigned';
  }, [headteacher, isStudent, recipientType, selectedLearnerRecord]);

  const handleSend = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!selectedLearner) {
      setError(isStudent ? 'Your student profile is not available for messaging.' : 'Select a student to continue.');
      return;
    }

    if (!body.trim()) {
      setError('Enter a message before sending.');
      return;
    }

    try {
      setSending(true);
      setError('');

      const payload: {
        learnerId: number;
        teacherId?: number;
        subject?: string;
        content: string;
      } = {
        learnerId: selectedLearner,
        subject: subject.trim(),
        content: body.trim()
      };

      if (isStudent) {
        if (recipientType === 'headteacher') {
          if (!headteacher) {
            setError('Headteacher contact is not available right now.');
            return;
          }
          payload.teacherId = headteacher.id;
        } else if (selectedLearnerRecord?.classTeacherId) {
          payload.teacherId = selectedLearnerRecord.classTeacherId;
        }
      }

      await createMessageThread(payload);
      await onSent();
      onClose();
      setSubject('');
      setBody('');
    } catch (sendError: any) {
      setError(sendError?.response?.data?.message || 'Unable to send message right now.');
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="messages-compose-backdrop" onClick={onClose}>
      <aside className="messages-compose-panel" onClick={(event) => event.stopPropagation()}>
        <div className="compose-panel-header">
          <div>
            <p className="section-eyebrow">Communications</p>
            <h2>Compose New Message</h2>
            <p>Search for a learner by name or grade, then send a clean in-system message from one focused workspace.</p>
          </div>
          <button type="button" className="compose-close-btn" onClick={onClose} aria-label="Close compose panel">
            <X size={18} />
          </button>
        </div>

        <form className="compose-form" onSubmit={handleSend}>
          {error ? <div className="compose-feedback error">{error}</div> : null}

          <div className="compose-grid">
            <label className="compose-field">
              <span>Sender</span>
              <div className="compose-static">{getDisplayName()}</div>
            </label>

            {!isStudent ? (
              <label className="compose-field">
                <span>Search learner or grade</span>
                <input
                  type="search"
                  value={learnerSearch}
                  onChange={(event) => setLearnerSearch(event.target.value)}
                  placeholder="Search by student name or grade"
                  disabled={loading || sending}
                />
              </label>
            ) : null}

            <label className="compose-field">
              <span>Student</span>
              {isStudent ? (
                <div className="compose-static">
                  {selectedLearnerRecord?.learnerName || (loading ? 'Loading profile...' : 'Student profile unavailable')}
                </div>
              ) : (
                <select
                  value={selectedLearner || ''}
                  onChange={(event) => setSelectedLearner(event.target.value ? Number(event.target.value) : null)}
                  disabled={loading || sending}
                >
                  <option value="">{filteredLearners.length === 0 ? 'No learners match your search' : 'Select student'}</option>
                  {filteredLearners.map((learner) => (
                    <option key={learner.learnerId} value={learner.learnerId}>
                      {learner.learnerName}{learner.className ? ` - ${learner.className}` : ''}
                    </option>
                  ))}
                </select>
              )}
            </label>
          </div>

          {isStudent ? (
            <div className="compose-field">
              <span>Recipient</span>
              <div className="compose-radio-group" role="radiogroup" aria-label="Recipient type">
                <label className={`compose-choice ${recipientType === 'classTeacher' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="recipient-type"
                    checked={recipientType === 'classTeacher'}
                    onChange={() => setRecipientType('classTeacher')}
                    disabled={!selectedLearnerRecord?.classTeacherId || sending}
                  />
                  <span>Class Teacher</span>
                </label>
                <label className={`compose-choice ${recipientType === 'headteacher' ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="recipient-type"
                    checked={recipientType === 'headteacher'}
                    onChange={() => setRecipientType('headteacher')}
                    disabled={!headteacher || sending}
                  />
                  <span>Headteacher</span>
                </label>
              </div>
              <div className="compose-helper">Sending to: {recipientLabel}</div>
            </div>
          ) : (
            <div className="compose-field">
              <span>Recipient</span>
              <div className="compose-static">{recipientLabel}</div>
            </div>
          )}

          <label className="compose-field">
            <span>Subject</span>
            <input
              type="text"
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
              placeholder="Example: Progress update or support request"
              disabled={sending}
            />
          </label>

          <label className="compose-field compose-field-message">
            <span>Message body</span>
            <textarea
              value={body}
              onChange={(event) => setBody(event.target.value)}
              placeholder="Write your message here..."
              rows={10}
              disabled={sending}
            />
          </label>

          <div className="compose-panel-footer">
            <button type="button" className="secondary-btn" onClick={onClose} disabled={sending}>
              Cancel
            </button>
            <button type="submit" className="primary-btn" disabled={sending || loading}>
              {sending ? 'Sending...' : 'Send Message'}
            </button>
          </div>
        </form>
      </aside>
    </div>
  );
};

export const MessageHome: React.FC = () => {
  const { unreadCount } = useOutletContext<MessagesOutletContext>();

  return (
    <section className="message-home-card">
      <div className="message-home-icon">
        <MessageSquare size={28} />
      </div>
      <p className="section-eyebrow">Conversation View</p>
      <h2>Select a conversation</h2>
      <p>Open a thread to read the full history, check message details, and send a reply.</p>
      {unreadCount > 0 ? (
        <div className="message-home-badge">
          <Inbox size={16} />
          <span>{unreadCount} unread message{unreadCount === 1 ? '' : 's'} waiting</span>
        </div>
      ) : null}
    </section>
  );
};

const MessagesPage: React.FC = () => {
  const navigate = useNavigate();
  const { id: activeThreadId } = useParams<{ id: string }>();
  const role = getRole();
  const canSeeAllSchoolThreads = role === 'admin' || role === 'headteacher';

  const [threads, setThreads] = useState<Thread[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeBox, setActiveBox] = useState<Mailbox>('inbox');
  const [readFilter, setReadFilter] = useState<ReadFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchMatches, setSearchMatches] = useState<Set<number> | null>(null);
  const [searching, setSearching] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isComposeOpen, setIsComposeOpen] = useState(false);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const nextUnreadCount = await getUnreadMessageCount();
      setUnreadCount(nextUnreadCount || 0);
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  }, []);

  const fetchThreads = useCallback(async (options?: { silent?: boolean }) => {
    try {
      if (!options?.silent) {
        setLoading(true);
      }

      const threadData = await getMessageThreads({
        archived: activeBox === 'archived',
        box: activeBox,
        all: canSeeAllSchoolThreads
      });
      setThreads(threadData || []);
    } catch (error) {
      console.error('Error fetching threads:', error);
      if (!options?.silent) {
        setThreads([]);
      }
    } finally {
      if (!options?.silent) {
        setLoading(false);
      }
    }
  }, [activeBox, canSeeAllSchoolThreads]);

  useEffect(() => {
    fetchThreads();
    fetchUnreadCount();
  }, [fetchThreads, fetchUnreadCount]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      fetchThreads({ silent: true });
      fetchUnreadCount();
    }, 10000);

    return () => window.clearInterval(intervalId);
  }, [fetchThreads, fetchUnreadCount]);

  useEffect(() => {
    const trimmedQuery = searchQuery.trim();
    if (trimmedQuery.length < 2) {
      setSearchMatches(null);
      setSearching(false);
      return undefined;
    }

    let active = true;
    const timeoutId = window.setTimeout(async () => {
      try {
        setSearching(true);
        const results = await searchMessages({
          q: trimmedQuery,
          readStatus: readFilter === 'all' ? undefined : readFilter === 'read',
          all: canSeeAllSchoolThreads
        });
        if (!active) {
          return;
        }

        setSearchMatches(new Set((results || []).map((result: { threadId: number }) => result.threadId)));
      } catch (error) {
        console.error('Error searching messages:', error);
        if (active) {
          setSearchMatches(new Set());
        }
      } finally {
        if (active) {
          setSearching(false);
        }
      }
    }, 300);

    return () => {
      active = false;
      window.clearTimeout(timeoutId);
    };
  }, [canSeeAllSchoolThreads, readFilter, searchQuery]);

  const filteredThreads = useMemo(() => {
    return threads.filter((thread) => {
      const matchesReadFilter =
        readFilter === 'all' ||
        (readFilter === 'unread' ? thread.unreadCount > 0 : thread.unreadCount === 0);

      const matchesLocalSearch =
        !searchQuery.trim() ||
        thread.subject?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        thread.lastMessage?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        thread.learnerName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        thread.teacherName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        thread.lastSenderName?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesServerSearch = searchMatches === null || searchMatches.has(thread.threadId);
      return matchesReadFilter && matchesLocalSearch && matchesServerSearch;
    });
  }, [readFilter, searchMatches, searchQuery, threads]);

  const threadStats = useMemo(() => {
    const total = threads.length;
    const unread = threads.filter((thread) => thread.unreadCount > 0).length;
    return { total, unread };
  }, [threads]);

  const threadEmptyText =
    activeBox === 'archived'
      ? 'No archived messages yet'
      : activeBox === 'sent'
        ? 'No sent conversations yet'
        : 'No messages yet';
  const activeMailbox = mailboxConfig.find((mailbox) => mailbox.id === activeBox);

  return (
    <div className={`messages-page-shell ${activeThreadId ? 'has-active-thread' : ''}`}>
      <div className="messages-page-header">
        <p className="section-eyebrow">Communications</p>
        <h1>Messages</h1>
        <p>Manage school conversations, replies, and follow-ups from one professional workspace.</p>
      </div>

      <div className={`messages-workspace ${activeThreadId ? 'has-active-thread' : ''}`}>
        <aside className="messages-sidebar">

        <button type="button" className="primary-btn sidebar-primary-btn" onClick={() => setIsComposeOpen(true)}>
          <Plus size={16} />
          <span>Compose</span>
        </button>

        <nav className="mailbox-nav" aria-label="Message folders">
          {mailboxConfig.map((mailbox) => {
            const Icon = mailbox.icon;
            const badgeCount = mailbox.id === 'inbox' ? unreadCount : undefined;
            const isActive = mailbox.id === activeBox;
            return (
              <button
                key={mailbox.id}
                type="button"
                className={`mailbox-nav-item ${isActive ? 'active' : ''}`}
                onClick={() => {
                  setActiveBox(mailbox.id);
                  navigate('/messages');
                }}
              >
                <span className="mailbox-nav-icon">
                  <Icon size={18} />
                </span>
                <span className="mailbox-nav-copy">
                  <strong>{mailbox.label}</strong>
                  <small>{mailbox.description}</small>
                </span>
                {badgeCount ? <span className="mailbox-badge">{badgeCount}</span> : null}
              </button>
            );
          })}
        </nav>

        <button type="button" className="secondary-btn sidebar-secondary-btn" onClick={() => fetchThreads()} aria-label="Refresh messages">
          <RefreshCw size={15} />
          <span>Refresh</span>
        </button>

        <div className="sidebar-summary-card compact">
          <div>
            <span>Total threads</span>
            <strong>{threadStats.total}</strong>
          </div>
          <div>
            <span>Unread threads</span>
            <strong>{threadStats.unread}</strong>
          </div>
        </div>
        </aside>

        <section className="messages-thread-panel">
        <div className="messages-toolbar">
          <div className="messages-search">
            <Search size={18} />
            <input
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search by subject, sender, student, grade, or message text"
              aria-label="Search messages"
            />
          </div>

          <div className="messages-toolbar-actions">
            <div className="messages-filter-group" role="group" aria-label="Read filter">
              <button
                type="button"
                className={readFilter === 'all' ? 'active' : ''}
                onClick={() => setReadFilter('all')}
              >
                <Filter size={14} />
                <span>All</span>
              </button>
              <button
                type="button"
                className={readFilter === 'unread' ? 'active' : ''}
                onClick={() => setReadFilter('unread')}
              >
                Unread
              </button>
              <button
                type="button"
                className={readFilter === 'read' ? 'active' : ''}
                onClick={() => setReadFilter('read')}
              >
                Read
              </button>
            </div>

          </div>
        </div>

        <div className="thread-panel-header">
          <div className="thread-panel-header-copy">
            <p className="section-eyebrow">{activeBox}</p>
            <h2>{activeMailbox?.label}</h2>
            <p>{activeMailbox?.description}. Keep track of responses, unread items, and follow-ups.</p>
            <div className="thread-panel-summary">
              <span className="thread-count-pill">{filteredThreads.length} shown</span>
              <span className="thread-count-pill strong">{threadStats.unread} unread</span>
            </div>
          </div>
          {searching ? <span className="thread-panel-status">Searching...</span> : null}
        </div>

        <div className="thread-list">
          {loading ? (
            <div className="messages-state-card">
              <div className="pro-loader" aria-label="Loading" />
              <p>Loading conversations...</p>
            </div>
          ) : filteredThreads.length === 0 ? (
            <div className="messages-state-card empty">
              <div className="messages-state-icon">
                <MessageSquare size={28} />
              </div>
              <h3>{threadEmptyText}</h3>
              <p>
                {searchQuery.trim()
                  ? 'Try a different search or filter to find the conversation you need.'
                  : 'Start a new conversation to contact your teacher, learner, or school office.'}
              </p>
            </div>
          ) : (
            filteredThreads.map((thread) => {
              const threadPartner = role === 'student' ? thread.teacherName : thread.learnerName;
              const isActive = String(thread.threadId) === activeThreadId;
              const lastActivity = formatThreadTimestamp(thread.lastMessageAt || thread.createdAt);
              const participantBadge = role === 'student' ? (thread.teacherRole || 'Staff') : 'Student';
              return (
                <button
                  key={thread.threadId}
                  type="button"
                  className={`thread-list-item ${thread.unreadCount > 0 ? 'unread' : ''} ${isActive ? 'active' : ''}`}
                  onClick={() => navigate(`/messages/${thread.threadId}`)}
                >
                  <div className="thread-list-avatar">
                    <UserRound size={18} />
                  </div>
                  <div className="thread-list-copy">
                    <div className="thread-list-topline">
                      <strong>{threadPartner || 'Conversation'}</strong>
                      <span>{lastActivity}</span>
                    </div>
                    <div className="thread-list-meta">
                      <span className="thread-participant-badge">{participantBadge}</span>
                      <span>{thread.subject || 'No subject'}</span>
                      {thread.className ? <span>{thread.className}</span> : null}
                    </div>
                    <p>{buildThreadPreview(thread)}</p>
                  </div>
                  {thread.unreadCount > 0 ? <span className="thread-unread-pill">{thread.unreadCount}</span> : null}
                </button>
              );
            })
          )}
        </div>
        </section>

        <section className="messages-detail-panel">
          <Outlet
            context={{
              refreshThreads: fetchThreads,
              fetchUnreadCount,
              unreadCount,
              activeBox
            } satisfies WorkspaceOutletContext}
          />
        </section>
      </div>

      <ComposePanel
        isOpen={isComposeOpen}
        onClose={() => setIsComposeOpen(false)}
        onSent={async () => {
          await fetchThreads();
          await fetchUnreadCount();
        }}
      />
    </div>
  );
};

export default MessagesPage;
